/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { createSlice } from "@reduxjs/toolkit";
import { Logger } from "cms/utils/log/logger";
import { webpageState } from "./webpage.state";
import { 
    fetchPublishStatus,
    fetchWebpage,
    refreshWebpage
} from "./webpage.thunks";

const webpage = createSlice({
    name: "webpage",

    initialState: webpageState,

    reducers: {
        setWebpageItem (state, action) {
            const { updateCount, ...webpage } = action.payload;
            state.current = webpage;
            state.current_guid = webpage.guid;
            state.webpage = webpage;

            if (updateCount) {
                state.settingsUpdateCount++;
            }
        },
        
        setWebpageProperty (state, action) {
            const { key, value } = action.payload;
            state.current[key] = value;
        },
        
        setLoading (state, action) {
            state.loading = action.payload;
        },
        
        setPublishStatusLoading (state, action) {
            state.publishStatusLoading = action.payload;
        },
        
        setCurrentWebpageGuid (state, action) {
            const guid = action.payload;
            state.current_guid = guid;
        },
        
        setCurrentWebpage (state, action) {
            const webpage = action.payload;
            state.current = webpage;
            const guid = webpage.guid;
            state.current_guid = guid;
        },
        
        setParentWebpage (state, action) {
            const parent = action.payload;
            state.current.parent = parent.guid;
            state.parent = parent;
        },
        
        removeParentWebpage (state) {
            state.current.parent = null;
            state.parent = undefined;
        },
        
        setHoldNav (state, action) {
            const value = action.payload;
            state.holdNav = value;
        },
        
        setChangeNeeded (state, action) {
            const value = action.payload;
            state.changeNeeded = value;
        },
        
        setReloadRequired (state, action) {
            const value = action.payload;
            state.reloadRequired = value;
        },
        
        setSaveDisabled (state, action) {
            const value = action.payload;
            state.saveDisabled = value;
        },
    },

    extraReducers (builder) {

        // fetch webpage
        builder.addCase(fetchWebpage.pending, (state) => {
            state.loading = true;
        });

        builder.addCase(fetchWebpage.fulfilled, (state, action) => {
            state.current = action.payload;
            state.loading = false;
        });

        builder.addCase(fetchWebpage.rejected, (state, action) => {
            // todo: properly record errors
            Logger.error("fetchWebpage has an error: ", action.error);
            state.loading = false;
        });

        // refresh webpage, similar to fetch webpage, but does not set loading
        // and does not update parent and children
        builder.addCase(refreshWebpage.fulfilled, (state, action) => {
            state.current = {
                ...state.current,
                ...action.payload,
            };
        });

        builder.addCase(refreshWebpage.rejected, (state, action) => {
            // todo: properly record errors
            Logger.error("refreshWebpage has an error: ", action.error);
        });

        // fetchPublishStatus
        builder.addCase(fetchPublishStatus.pending, (state) => {
            state.publishStatusLoading = true;
        });
        
        builder.addCase(fetchPublishStatus.fulfilled, (state, action) => {
            const { publishStatus } = action.payload;
            state.publishStatus = publishStatus;
            state.publishStatusLoading = false;
        });
        
        builder.addCase(fetchPublishStatus.rejected, (state, action) => {
            // todo: properly record errors
            Logger.error("fetchPublishStatus has an error: ", action.error);
            state.publishStatusLoading = false;
        });
    }
});

export const webpageReducer = webpage.reducer;

export const {
    setWebpageItem,
    setWebpageProperty,
    setCurrentWebpage,
    setCurrentWebpageGuid,
    setParentWebpage,
    removeParentWebpage,
    setHoldNav,
    setChangeNeeded,
    setReloadRequired,
    setSaveDisabled,
} = webpage.actions;
